import React, { useState } from 'react';
import {
    ClipboardCheck,
    AlertTriangle,
    TrendingUp,
    CheckCircle,
    Calendar,
    Layout,
    List,
    AlertCircle
} from 'lucide-react';
import FloatingParticles from './FloatingParticles';
import PageHeader from './ui/PageHeader';
import QualityKanban from './quality/QualityKanban';
import QualityTimeline from './quality/QualityTimeline';
import { useQualityData } from '../hooks/useApi';
import '../styles/neutral-theme.css';
import '../styles/analytics-metrics.css';
import '../styles/analytics-headers-override.css';
import '../styles/analytics-cards-override.css';
import '../styles/analytics-evolution-modern.css';
import '../styles/analytics-campaign-results.css';

const QualityPlanPanel = () => {
    const today = new Date().toISOString().split('T')[0];
    // Default to last 30 days
    const lastMonth = new Date();
    lastMonth.setDate(lastMonth.getDate() - 30);
    const lastMonthStr = lastMonth.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(lastMonthStr);
    const [endDate, setEndDate] = useState(today);
    const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'timeline'

    // Fetch real data
    const { tasks, loading, error, refetch } = useQualityData({
        startDate,
        endDate
    });

    // Calculate KPIs from tasks
    const totalTasks = tasks?.length || 0;
    const completedTasks = tasks?.filter(t => t.status === 'Completado')?.length || 0;
    const inProgressTasks = tasks?.filter(t => t.status === 'En Progreso')?.length || 0;
    const pendingTasks = tasks?.filter(t => t.status === 'Pendiente')?.length || 0;
    const failedTasks = tasks?.filter(t => t.status === 'Fallos')?.length || 0;
    const revisionTasks = tasks?.filter(t => t.status === 'Revisión')?.length || 0;

    // Calculate completion rate
    const completionRate = totalTasks > 0
        ? ((completedTasks / totalTasks) * 100).toFixed(1)
        : 0;

    const kpis = [
        {
            label: 'TOTAL TAREAS',
            value: totalTasks,
            color: '#e0f2fe',
            textColor: '#0369a1',
            borderColor: '#bae6fd',
            icon: <ClipboardCheck size={20} />
        },
        {
            label: 'EN PROGRESO',
            value: inProgressTasks,
            color: '#dbeafe',
            textColor: '#1e40af',
            borderColor: '#bfdbfe',
            icon: <TrendingUp size={20} />
        },
        {
            label: 'PENDIENTES',
            value: pendingTasks,
            color: '#ffedd5',
            textColor: '#9a3412',
            borderColor: '#fed7aa',
            icon: <AlertCircle size={20} />
        },
        {
            label: 'TASA DE COMPLETADO',
            value: `${completionRate}%`,
            color: '#ffffff',
            textColor: '#0c2c6c',
            borderColor: 'rgba(12, 44, 108, 0.1)',
            isMain: true,
            icon: <CheckCircle size={20} />
        }
    ];

    return (
        <div className="quality-plan-panel-vertical dashboard-container" style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: '100%',
            gap: '24px'
        }}>
            <FloatingParticles count={25} />

            <div style={{ width: '100%', display: 'block' }}>
                <PageHeader
                    title={'Plan de Calidad'}
                    subtitle="Gestión de tareas, auditorías y seguimiento de calidad"
                    icon={<ClipboardCheck />}
                />
            </div>

            {/* ERROR / LOADING STATE */}
            {error && (
                <div style={{ padding: '10px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', border: '1px solid #fecaca' }}>
                    Error cargando datos: {error}
                </div>
            )}

            {/* KPI Section */}
            <div className="metrics-grid" style={{ marginBottom: '24px' }}>
                {kpis.map((kpi, index) => (
                    <div key={index} className="metric-card-ultra-modern">
                        <div className="metric-header-ultra">
                            <div className="metric-label-container-ultra">
                                <span className="metric-label-ultra">{kpi.label}</span>
                            </div>
                            <div className="metric-icon-ultra" style={{ color: kpi.textColor }}>
                                {kpi.icon || <TrendingUp size={16} />}
                            </div>
                        </div>

                        <div className="metric-value-container-ultra">
                            <div className="metric-value-ultra" style={{ color: kpi.isMain ? '#fff' : kpi.textColor }}>
                                {loading ? '...' : kpi.value}
                            </div>
                            <div className="metric-value-glow"></div>
                        </div>

                        <div className="metric-background-ultra" style={{
                            background: kpi.isMain ? 'linear-gradient(135deg, #0f172a 0%, #334155 100%)' : kpi.color,
                            opacity: kpi.isMain ? 1 : 0.5
                        }}></div>
                    </div>
                ))}
            </div>

            {/* Filter & View Controls */}
            <div className="card card-enhanced" style={{
                marginBottom: '24px',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#ffffff',
                border: '1px solid rgba(12, 44, 108, 0.1)'
            }}>
                {/* Date Filter */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <Calendar size={16} color="#64748b" />
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ background: 'transparent', border: 'none', fontSize: '14px', color: '#334155', fontWeight: 500, outline: 'none', fontFamily: 'inherit' }}
                        />
                        <span style={{ color: '#cbd5e1' }}>—</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ background: 'transparent', border: 'none', fontSize: '14px', color: '#334155', fontWeight: 500, outline: 'none', fontFamily: 'inherit' }}
                        />
                    </div>
                </div>

                {/* Search (Client side filtering planned later, or add to API) */}
                <div style={{ flex: 1, maxWidth: '400px', margin: '0 24px' }}>
                    <input
                        type="text"
                        placeholder="Buscar tareas, indicadores o áreas..."
                        style={{
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#f8fafc',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'all 0.2s'
                        }}
                    />
                </div>

                {/* View Toggle */}
                <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                    <button
                        onClick={() => setViewMode('kanban')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: viewMode === 'kanban' ? '#ffffff' : 'transparent',
                            color: viewMode === 'kanban' ? '#0f172a' : '#64748b',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            boxShadow: viewMode === 'kanban' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Layout size={16} /> Kanban
                    </button>
                    <button
                        onClick={() => setViewMode('timeline')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: viewMode === 'timeline' ? '#ffffff' : 'transparent',
                            color: viewMode === 'timeline' ? '#0f172a' : '#64748b',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            boxShadow: viewMode === 'timeline' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        <List size={16} /> Timeline
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div style={{ position: 'relative', minHeight: '500px' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        Cargando...
                    </div>
                ) : (
                    <>
                        {viewMode === 'kanban' && <QualityKanban tasks={tasks} onRefresh={refetch} />}
                        {viewMode === 'timeline' && <QualityTimeline data={tasks} />}
                    </>
                )}
            </div>
        </div>
    );
};

export default QualityPlanPanel;
